/**
 * Structure extraction: derives the exam's task tree and asks the teacher to confirm it.
 *
 * The step ENDS at the confirmation request — it does not wait for the answer. Everything that
 * depends on a confirmed structure is queued when the confirmation arrives (see `confirmStructureMutation`,
 * which dispatches the extraction jobs).
 * This is the whole point of the split: the previous version held one in-process promise open for up
 * to ~16 hours against an in-memory listener, so a server restart during the teacher's review left
 * the exam permanently stuck — the pipeline could not be resumed and its restart guard refused to run
 * it again.
 */

import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { STRUCTURE_EDITOR_BBX_THRESHOLDS } from '../../../shared/constants.js';
import { ConfirmationProgressStatus, setStructureConfirmationProgressByExam } from '../../confirmation-progress.js';
import { ConfirmationMessage, Conversation } from '../../conversation.js';
import { Exam } from '../../exam.js';
import {
  clearFinishedUnits,
  createStep,
  setStatusByExam,
  setTotalByExam,
  StepState,
  updateMessageByExam,
} from '../../extraction-progress.js';
import { normalizeSubject } from '../../subjects.js';
import { bbxKind } from '../agents/bbx-kind.js';
import { applySemanticConfidenceToRecursiveTasks } from '../agents/calculate-task-extraction-confidence.js';
import { runBoundingBoxAgent } from '../agents/extraction-modules/bounding-box.js';
import {
  buildAdditionalInfo,
  buildExamTasksFromRecursiveStructure,
} from '../agents/extraction-modules/build-exam-tasks.js';
import { formatStructureForDisplay } from '../agents/extraction-modules/confirmation-formatting.js';
import { checkRecursiveTasksForLowConfidence } from '../agents/extraction-modules/get_low_confidence_extracted_tasks.js';
import { runStructureAgent } from '../agents/extraction-modules/structure.js';
import { enrichTasksWithOCRByBbx, parseAgentAnswer } from '../agents/extraction-modules/task-enrichment.js';
import { getSubmissionsByExamIdQuery } from '../get-submissions.js';
import {
  createSetExamMetadataPendingTask,
  SET_EXAM_METADATA_TASK_CLASS,
} from '../pending-tasks/set-exam-metadata.task.js';
import { countRecursiveTasks, createCostTracker, serializeTaskTree, type StepResult } from './step-utils.js';

export interface StructureStepResult extends StepResult {
  /** Extraction flagged uncertain regions — the canvas is opened directly instead of just offered. */
  hasLowConfidence: boolean;
  /** The rendered structure summary shown in the confirmation message. */
  confirmationMessage?: string;
}

const FAILED: StructureStepResult = { ok: false, skipped: false, hasLowConfidence: false };

/**
 * The images the structure is read from: the blank exam if the teacher uploaded one, otherwise the
 * first submission. The choice is PERSISTED to `blankExamIds` rather than kept in memory, so every
 * later step (and every retry) reads the same source.
 */
async function resolveStructureSource(examId: string, exam: any): Promise<string[] | null> {
  const blankExamIds: string[] = Array.isArray(exam.blankExamIds) ? exam.blankExamIds : [];
  if (blankExamIds.length > 0) return blankExamIds;

  const submissions = await getSubmissionsByExamIdQuery(examId)({ _id: 1, imageIds: 1 });
  const firstSubmissionImageIds = submissions[0]?.imageIds;
  if (!Array.isArray(firstSubmissionImageIds) || firstSubmissionImageIds.length === 0) return null;

  const examCollection = await getCollection(Exam as any);
  await examCollection.updateOne({ _id: examId } as any, { $set: { blankExamIds: firstSubmissionImageIds } });
  logger.info({ examId }, 'Saved firstSubmission images as blankExamIds for exam');
  return firstSubmissionImageIds;
}

/**
 * Queues the exam metadata (subject/topic/class) the structure agent read off the sheet as a pending
 * task, so the agent confirms it with the teacher via `set_metadata`.
 *
 * Metadata comes from the STRUCTURE source, not from the split: the split can now run after the master
 * solution and the grading scheme (blank exam first, submissions later), while generation needs subject
 * and topic — and the structure always precedes both. Never fatal: without it the teacher sets the
 * metadata by hand.
 */
async function queueExamMetadataTask(examId: string, examCollection: any, rawMeta: unknown): Promise<void> {
  if (!rawMeta || typeof rawMeta !== 'object') return;

  const meta = rawMeta as Record<string, unknown>;
  // Coerce to a canonical subject key so only enum values ever reach storage (defends against a model
  // that ignores the prompt). normalizeSubject falls back to 'other'.
  const subject = normalizeSubject(meta.subject);
  const topic = String(meta.topic ?? '').trim();
  const classValue = typeof meta.class === 'string' && meta.class.trim() ? meta.class.trim() : undefined;

  // `subject` is always a key (>= 'other'), so a bare 'other' with no topic means nothing was found.
  if (!topic && subject === 'other') return;

  try {
    const exam = await examCollection.findOne(
      { _id: examId } as any,
      {
        projection: { 'examState.metaDataSet': 1, 'examState.pendingTasks': 1 },
      } as any,
    );
    const alreadySet = Boolean(exam?.examState?.metaDataSet);
    const alreadyQueued =
      Array.isArray(exam?.examState?.pendingTasks) &&
      exam.examState.pendingTasks.some((task: any) => task?._class === SET_EXAM_METADATA_TASK_CLASS);
    if (alreadySet || alreadyQueued) return;

    await examCollection.updateOne(
      { _id: examId } as any,
      {
        $push: {
          'examState.pendingTasks': createSetExamMetadataPendingTask({
            subject,
            topic: topic || 'Unbekannt',
            ...(classValue ? { class: classValue } : {}),
          }),
        },
      } as any,
    );
    logger.info(
      { examId, subject, topic, class: classValue ?? 'N/A' },
      'Structure step: queued exam metadata for confirmation',
    );
  } catch (err) {
    logger.error({ err, examId }, 'Structure step: failed to queue exam metadata');
  }
}

/** Posts the confirmation request and marks the structure gate as awaiting the teacher. */
export async function requestStructureConfirmation(
  examId: string,
  conversationId: string,
  content: string,
  hasLowConfidence: boolean,
): Promise<void> {
  await updateMessageByExam(examId, 'task', 'progress.waitingForStructureConfirmation');

  const confirmationMsg = new ConfirmationMessage({
    content,
    confirmed: false,
    confirmedBy: 'pending',
    confirmationType: 'structure',
  });

  const conversationCollection = await getCollection(Conversation);
  await conversationCollection.updateOne(
    { _id: conversationId },
    {
      $push: { messages: (confirmationMsg as any).toObject?.() ?? confirmationMsg },
      $set: {
        lastUpdated: new Date(),
        ...(hasLowConfidence ? { pendingCanvasOpen: 'structureeditor' } : {}),
      },
    },
  );

  await setStructureConfirmationProgressByExam(examId, {
    status: ConfirmationProgressStatus.RUNNING,
    confirmed: false,
    messageId: String((confirmationMsg as any)?._id ?? '').trim(),
    message: 'progress.waitingForStructureConfirmation',
  });
}

export async function runStructureStep(examId: string, conversationId?: string): Promise<StructureStepResult> {
  const examCollection = await getCollection(Exam as any);
  const exam = await examCollection.findOne({ _id: examId } as any);
  if (!exam) {
    logger.error({ examId }, 'Structure step: exam not found');
    return FAILED;
  }

  // The records for everything downstream are seeded by the DISPATCH, not here — see `onStepStart` in
  // `structure.job.ts`. They existed here as resets, which wiped an answer run that an upload had already opened.
  //
  // `task` IS this step's own record, so resetting it is right: a re-extraction really does start its counters over.
  await createStep('task', examId);
  await setStatusByExam(examId, 'task', StepState.IN_PROGRESS, 'progress.analyzingStructure');

  const cost = createCostTracker(examId);

  const structureSourceIds = await resolveStructureSource(examId, exam);
  if (!structureSourceIds) {
    logger.error({ examId }, 'Structure step: neither a blank exam nor a submission to read from');
    await setStatusByExam(examId, 'task', StepState.ERROR, 'progress.noSubmissions');
    return FAILED;
  }

  let tasks: any[];
  try {
    logger.info({ examId, structureSourceIds }, 'Running structure agent for exam');
    const structureResult = await runStructureAgent(structureSourceIds);
    cost.addCost(structureResult?.cost, 'structureAgent');
    const parsedStructure = parseAgentAnswer(structureResult);

    // Independent of the task tree, so it is queued before anything else can fail.
    await queueExamMetadataTask(examId, examCollection, parsedStructure?.metadata);

    if (!parsedStructure?.tasks || !Array.isArray(parsedStructure.tasks) || parsedStructure.tasks.length === 0) {
      await setStatusByExam(examId, 'task', StepState.ERROR, 'progress.noTasksInStructure');
      await cost.flush();
      return FAILED;
    }
    tasks = parsedStructure.tasks;
  } catch (err) {
    logger.error({ err, examId }, 'Error occurred while running structure agent for exam');
    await setStatusByExam(examId, 'task', StepState.ERROR, 'progress.errorStructureAnalysis');
    await cost.flush();
    return FAILED;
  }

  await updateMessageByExam(examId, 'task', 'progress.extractingTaskDescriptions');
  // EVERY NODE, not just the main tasks, because the enrichment below is what advances this bar and it works
  // per node — see the granularity note in `task-enrichment.ts`. Main tasks are the unit of the steps whose
  // JOBS are per main task; here the four roots are awaited together and would show one jump from 0 to 4.
  //
  // Cleared first because a re-extraction REBUILDS the tree: the previous run's units describe tasks that no
  // longer exist, and `markUnitFinished` is an `$addToSet`, so without this they would be carried forward and
  // the bar could report more finished units than the new total has.
  await clearFinishedUnits(examId, 'task');
  await setTotalByExam(examId, 'task', countRecursiveTasks(tasks));

  // Task descriptions. A failure here is not fatal — the structure itself is usable, so we mark the
  // step as errored but keep going with what we have.
  let descriptionsFailed = false;
  let bbxTasksResult: any = { answer: JSON.stringify({ tasks: [] }), cost: 0 };
  try {
    bbxTasksResult = await runBoundingBoxAgent(structureSourceIds, bbxKind.TASKS, JSON.stringify({ tasks }));
    cost.addCost(bbxTasksResult?.cost, 'structureBoundingBox');
  } catch (err) {
    logger.error({ err, examId }, 'BBX extraction for tasks error for exam');
    descriptionsFailed = true;
  }

  try {
    tasks = await enrichTasksWithOCRByBbx(
      tasks,
      parseAgentAnswer(bbxTasksResult),
      structureSourceIds,
      'description',
      false,
      c => cost.addCost(c, 'structureOcr'),
      examId,
      'task',
    );
  } catch (err) {
    logger.error({ err, examId }, 'Task description extraction error for exam');
    descriptionsFailed = true;
  }

  if (descriptionsFailed) {
    await setStatusByExam(examId, 'task', StepState.ERROR, 'progress.errorTaskDescriptions');
  } else {
    await updateMessageByExam(examId, 'task', 'progress.taskDescriptionsExtracted');
  }

  try {
    let bbxData: any = { additionalInformation: [], tasks: [] };
    try {
      bbxData = JSON.parse(typeof bbxTasksResult === 'string' ? bbxTasksResult : bbxTasksResult?.answer || '{}');
    } catch {
      logger.warn({ examId }, 'Invalid BBX output JSON, skipping additional information');
    }

    const additionalInfoList = await buildAdditionalInfo(
      bbxData,
      structureSourceIds,
      c => cost.addCost(c, 'structureAdditionalInfoOcr'),
      examId,
      'task',
      examId,
    );

    tasks = await applySemanticConfidenceToRecursiveTasks({
      tasks,
      additionalInformation: additionalInfoList,
      onCost: c => cost.addCost(c, 'structureSemanticConfidence'),
    });

    // The only build that seeds own points from the declared totals: this is the one-time guess the
    // teacher then confirms. Every later rebuild carries the confirmed values forward instead.
    const examTasks = await buildExamTasksFromRecursiveStructure(tasks, examId, undefined, {
      seedOwnPointsFromTotals: true,
    });

    await examCollection.updateOne({ _id: examId } as any, {
      $set: {
        tasks: serializeTaskTree(examTasks),
        additionalInformation: additionalInfoList.map(a => a.toObject()),
      },
    });
  } catch (err) {
    logger.error({ err, examId }, 'Create exam tasks error');
    await setStatusByExam(examId, 'task', StepState.ERROR, 'progress.errorCreatingTaskStructure');
    await cost.flush();
    return FAILED;
  }

  if (!descriptionsFailed) {
    await setStatusByExam(examId, 'task', StepState.COMPLETED, 'progress.taskStructureCreated');
  }

  const hasLowConfidence = checkRecursiveTasksForLowConfidence(tasks, STRUCTURE_EDITOR_BBX_THRESHOLDS.HIGH_CONFIDENCE);
  logger.info({ examId, hasLowConfidence }, 'Computed hasLowConfidence for exam');

  let confirmationMessage: string | undefined;
  if (conversationId) {
    const conversationCollection = await getCollection(Conversation);
    const conversation = await conversationCollection.findOne({ _id: conversationId }, {
      projection: { language: 1 },
    } as any);
    const language =
      typeof (conversation as any)?.language === 'string' ? String((conversation as any).language).trim() : '';
    confirmationMessage = formatStructureForDisplay(tasks, language || 'de');
  }

  await cost.flush();
  return { ok: true, skipped: false, hasLowConfidence, confirmationMessage };
}
